/*
 * Copyright (c) 2023 Bolmate B.V.
 */
import './style/returnDetails.scss';

import React, {MouseEventHandler, useRef} from 'react';

import {useTranslation} from 'react-i18next';

import {
    AwesomeIcon,
    CloseButton,
    Link,
    Loader,
    MenuPlacement,
    OptionsMenu,
    OptionsMenuItem,
    WithBackground
} from '@dvrd/dvr-controls';

import BolReturn from '../../../models/bolReturnModel';

import {BolmateButton} from '../../controls';

import Image from '../../misc/image';

interface Props {
    onClose: MouseEventHandler;
    onClickInvoice: (invoiceId: string) => MouseEventHandler;
    onClickSend: (invoiceId: string) => MouseEventHandler;
    onClickCredit: MouseEventHandler;
    onClickBol: MouseEventHandler;
    active: boolean;
    bolReturn: BolReturn;
    loading: boolean;
}

export default function ReturnDetails(props: Props) {
    const {t} = useTranslation();
    const {onClickCredit, active, onClickSend, onClose, onClickInvoice, bolReturn, loading, onClickBol} = props,
        {
            product,
            returnId,
            invoice,
            translatedHandling,
            translatedStatus,
            registeredAtMoment,
            orderId,
            rmaId,
            processedAtMoment,
            creditInvoiceId,
            creditSent,
            invoiceId,
            creditInvoice,
            returnReason,
            returnDetail
        } = bolReturn,
        optionsRef = useRef<OptionsMenu>(null);

    function getOptions(): OptionsMenuItem[] {
        const options: OptionsMenuItem[] = [];
        if (invoiceId)
            options.push({
                label: t('returns.details.toInvoice', 'Naar factuur'),
                onClick: onClickInvoice(invoiceId),
                icon: <AwesomeIcon name='receipt' className='icon' />
            });
        if (creditInvoiceId)
            options.push(
                {
                    label: t('returns.details.toCreditInvoice', 'Naar creditfactuur'),
                    onClick: onClickInvoice(creditInvoiceId),
                    icon: <AwesomeIcon name='euro-sign' className='icon' />
                },
                {
                    label: t('returns.details.sendCreditInvoice', 'Creditfactuur verzenden'),
                    onClick: onClickSend(creditInvoiceId),
                    icon: <AwesomeIcon name='paper-plane' className='icon' />
                }
            );
        else
            options.push({
                label: t('returns.details.credit', 'Crediteren'),
                onClick: onClickCredit,
                icon: <AwesomeIcon name='euro-sign' className='icon' />
            });
        options.push({
            label: t('returns.details.showOnBol', 'Weergeven op Bol'),
            onClick: onClickBol,
            icon: <Image name='bol-16' className='icon' alt='Bol' />
            // icon: <img src={getImg('bol-16')} alt='Bol' className='icon'/>
        });
        return options;
    }

    function renderImg() {
        if (!product) return <AwesomeIcon name='image' prefix='far' className='img-icon' />;
        return <img src={product.imageUrl} alt='Bolmate.nl' className='img' />;
    }

    function renderTitle() {
        let text: string;
        if (product) text = `${t('returns.details.returnPrefix', 'Retour')} ${returnId} - ${product.title}`;
        else text = `${t('returns.details.returnPrefix', 'Retour')} ${returnId}`;
        return <label className='title'>{text}</label>;
    }

    function renderData() {
        const optionsComponent = (
            <span className='return-options-comp'>
                <AwesomeIcon name='ellipsis-h' className='return-options-icon' />{' '}
                {t('returns.details.options', 'Opties')}
            </span>
        );
        return (
            <div className='data-container'>
                <OptionsMenu
                    options={getOptions()}
                    placement={MenuPlacement.BOTTOM_LEFT}
                    optionsComponent={optionsComponent}
                    containerClass='common-options return-options'
                    ref={optionsRef}
                />
                <div className='field'>
                    <label>{t('returns.details.registeredAt', 'Aangemeld op')}</label>
                    <label className='value'>{registeredAtMoment.format('DD MMMM YYYY HH:mm')}</label>
                </div>
                <div className='field'>
                    <label>{t('returns.details.processedAt', 'Verwerkt op')}</label>
                    <label className='value'>
                        {processedAtMoment?.format('DD MMMM YYYY HH:mm') ?? t('returns.details.na', 'n.v.t.')}
                    </label>
                </div>
                <div className='field'>
                    <label>{t('returns.details.customer', 'Klant')}</label>
                    <label className='value'>{invoice?.invoiceName}</label>
                </div>
                <div className='field'>
                    <label>{t('returns.details.orderNumber', 'Ordernummer')}</label>
                    <label className='value'>{orderId}</label>
                </div>
                <div className='field'>
                    <label>{t('returns.details.status', 'Status')}</label>
                    <label className='value'>{translatedStatus}</label>
                </div>
                <div className='field'>
                    <label>{t('returns.details.rmaId', 'RMA ID')}</label>
                    <label className='value'>{rmaId}</label>
                </div>
                <div className='field'>
                    <label>{t('returns.details.handling', 'Verwerking')}</label>
                    <label className='value'>{translatedHandling}</label>
                </div>
                <div className='field'>
                    <label>{t('returns.details.reason', 'Reden')}</label>
                    <label className='value'>{returnReason}</label>
                </div>
                <div className='field grid-col-2'>
                    <label>{t('returns.details.description', 'Omschrijving')}</label>
                    <label className='value'>{returnDetail}</label>
                </div>
            </div>
        );
    }

    function renderInvoices() {
        const hasCredit = creditInvoice !== null;
        return (
            <div className='invoices-container'>
                <div className='field'>
                    <label>{t('returns.details.originalInvoice', 'Originele factuur')}</label>
                    {invoice ? (
                        <Link onClick={onClickInvoice(invoice.id)}>{invoice.invoiceNum}</Link>
                    ) : (
                        <label className='value'>{t('returns.details.notFound', 'Niet gevonden')}</label>
                    )}
                </div>
                <div className='field'>
                    <label>{t('returns.details.creditInvoice', 'Creditfactuur')}</label>
                    {hasCredit ? (
                        <Link onClick={onClickInvoice(creditInvoice!.id)}>{creditInvoice!.invoiceNum}</Link>
                    ) : (
                        <label>{t('returns.details.notCredited', 'Niet gecrediteerd')}</label>
                    )}
                </div>
                {hasCredit && (
                    <div className='field'>
                        <label>{t('returns.details.creditInvoiceSent', 'Creditfactuur verzonden')}</label>
                        <label>{creditSent ? t('common.yes', 'Ja') : t('common.no', 'Nee')}</label>
                    </div>
                )}
            </div>
        );
    }

    return (
        <WithBackground onClose={onClose} active={active}>
            <div className='return-details default-popup'>
                <div className='body-container'>
                    <Loader active={loading} />
                    <div className='title-bar'>
                        <div className='img-container'>{renderImg()}</div>
                        {renderTitle()}
                        <CloseButton onClick={onClose} />
                    </div>
                    {renderData()}
                    {renderInvoices()}
                </div>
                <div className='footer-container'>
                    <BolmateButton onClick={onClose} label={t('returns.details.close', 'Sluiten')} secondary />
                </div>
            </div>
        </WithBackground>
    );
}
